/**
 * Permission-based access control helpers
 * Validates user roles before allowing API calls
 */

/**
 * Check if user can delete a course
 * @param {object} user - Current user object from auth context
 * @returns {boolean} True if user has permission
 */
function normalizeRole(user) {
  return String(user?.role || '').toUpperCase();
}

export function canDeleteCourse(user) {
  if (!user) return false;
  const r = normalizeRole(user);
  return r === 'ADMIN' || r === 'SUPERADMIN' || r === 'SUPER_ADMIN';
}

export function canEditCourse(user) {
  if (!user) return false;
  const r = normalizeRole(user);
  return (
    r === 'ADMIN' ||
    r === 'SUPERADMIN' ||
    r === 'SUPER_ADMIN' ||
    r === 'TEACHER' ||
    r === 'MENTOR'
  );
}

export function canCreateCourse(user) {
  if (!user) return false;
  const r = normalizeRole(user);
  return (
    r === 'ADMIN' ||
    r === 'SUPERADMIN' ||
    r === 'SUPER_ADMIN' ||
    r === 'TEACHER' ||
    r === 'MENTOR'
  );
}

export function canDeleteMessage(user, message) {
  if (!user || !message) return false;
  return (
    user.id === message.senderId ||
    user.role === 'ADMIN'
  );
}

export function canDeleteReply(user, reply) {
  if (!user || !reply) return false;
  return (
    user.id === reply.authorId ||
    user.role === 'TEACHER' ||
    user.role === 'ADMIN'
  );
}

export function canEditReply(user, reply) {
  if (!user || !reply) return false;
  return (
    user.id === reply.authorId ||
    user.role === 'ADMIN'
  );
}

export function canViewEnrollments(user) {
  if (!user) return false;
  const r = normalizeRole(user);
  return r === 'ADMIN' || r === 'SUPERADMIN' || r === 'SUPER_ADMIN' || r === 'TEACHER' || r === 'MENTOR';
}

export function canManageRoles(user) {
  if (!user) return false;
  const r = normalizeRole(user);
  return r === 'ADMIN' || r === 'SUPERADMIN' || r === 'SUPER_ADMIN';
}

export function validatePermission(user, action, context = {}) {
  let hasPermission = false;

  switch (action) {
    case 'deleteMessage':
      hasPermission = canDeleteMessage(user, context.message);
      break;
    case 'deleteReply':
      hasPermission = canDeleteReply(user, context.reply);
      break;
    case 'editReply':
      hasPermission = canEditReply(user, context.reply);
      break;
    case 'deleteCourse':
      hasPermission = canDeleteCourse(user);
      break;
    case 'editCourse':
      hasPermission = canEditCourse(user);
      break;
    case 'createCourse':
      hasPermission = canCreateCourse(user);
      break;
    case 'viewEnrollments':
      hasPermission = canViewEnrollments(user);
      break;
    case 'manageRoles':
      hasPermission = canManageRoles(user);
      break;
    default:
      throw new Error(`Unknown permission action: ${action}`);
  }

  if (!hasPermission) {
    throw new Error(`User lacks permission to perform action: ${action}`);
  }
}
